/**
 * Minimal fetch wrapper for the TradeLogic backend.
 *
 * - Base URL configurable via VITE_API_BASE_URL (default /api).
 *   In dev the Vite proxy rewrites /api -> /v1 so the request reaches
 *   the NestJS controllers mounted at /v1/* (see vite.config.ts).
 * - Auto-attaches Bearer access token from authBridge when no explicit
 *   token is supplied. Callers can still override with `init.token`.
 * - On HTTP 401 the wrapper asks authBridge to refresh the access token
 *   exactly once and retries the original request. If refresh fails or
 *   no refresh handler is registered, the original 401 ApiError is
 *   thrown so the caller (or AuthProvider) can clear state. (INT-012)
 * - Throws ApiError on non-2xx; callers map to UX.
 */

import { getAccessToken, triggerRefresh } from '../auth/authBridge.js';

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiRequestInit {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly token?: string | null;
  readonly headers?: Record<string, string>;
  /** Internal: skip the 401-refresh leg (used by the retry to avoid loops). */
  readonly _retried?: boolean;
}

const baseUrl: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL) ||
  '/api';

/** Same base every request uses; exported for downloads that bypass apiRequest. */
export const apiBaseUrl = baseUrl;

/**
 * A file the API serves, as an address this page can show (an image, say).
 * Fetched rather than linked so the login travels with it; the module that owns
 * the file decides who may read it. The caller revokes the address when done.
 */
export async function fetchApiFileUrl(path: string, token: string | null): Promise<string> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
    credentials: 'include',
  });
  if (!res.ok) await parseResponse(res); // throws the server's own message
  return URL.createObjectURL(await res.blob());
}

/** Opens a file the API serves, in a new tab. */
export async function openApiFile(path: string, token: string | null): Promise<void> {
  window.open(await fetchApiFileUrl(path, token), '_blank', 'noopener');
}

async function doFetch(path: string, init: ApiRequestInit, tokenOverride: string | null | undefined): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers ?? {}),
  };
  // FormData must set its own Content-Type: the browser appends the multipart
  // boundary, and overriding it makes the body unparseable on the server.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  const token = tokenOverride === undefined ? getAccessToken() : tokenOverride;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(`${baseUrl}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body:
      init.body === undefined
        ? undefined
        : isFormData
          ? (init.body as FormData)
          : JSON.stringify(init.body),
    // SEC-001: send the httpOnly `refresh_token` cookie on /auth/refresh
    // and any other endpoint that uses cookie-based session state. Safe
    // for non-auth calls — the cookie is Path=/v1/auth so the browser
    // won't attach it to other paths.
    credentials: 'include',
  });
}

async function parseResponse<TResponse>(res: Response): Promise<TResponse> {
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    // GlobalExceptionFilter answers with { code, user_message }, while a few
    // routes still return Nest's default { message }. Reading only `message`
    // showed every filtered error in the UI as a bare "HTTP 400".
    const obj = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    const detail =
      obj && typeof obj.user_message === 'string'
        ? obj.user_message
        : obj && 'message' in obj
          ? String(obj.message)
          : null;
    const code = obj && typeof obj.code === 'string' ? obj.code : null;
    // StrictValidationPipe puts the rule that refused each field in `errors`
    // under a generic user_message; without them the operator learns only that
    // something failed validation, never which rule (e.g. a GSTIN checksum).
    const fieldMessages = obj && Array.isArray(obj.errors)
      ? [...new Set(obj.errors
        .map((e) => (e && typeof (e as { message?: unknown }).message === 'string' ? (e as { message: string }).message : null))
        .filter((m): m is string => !!m))]
      : [];
    const fullDetail = detail && fieldMessages.length ? `${detail} ${fieldMessages.join(' ')}` : detail;
    const msg = fullDetail
      ? (code ? `${fullDetail} (${code})` : fullDetail)
      : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, msg);
  }
  return parsed as TResponse;
}

export async function apiRequest<TResponse>(path: string, init: ApiRequestInit = {}): Promise<TResponse> {
  // Auth endpoints don't go through the refresh interceptor: they either
  // ARE the refresh call or they're login/logout which control the token.
  // Callers pass the versioned path (/v1/auth/...), so an unversioned test
  // would never match and the refresh call would re-enter triggerRefresh and
  // await its own in-flight promise.
  const isAuthEndpoint = /^\/(?:v\d+\/)?auth\//.test(path);

  const tokenOverride = 'token' in init ? init.token : undefined;
  let res = await doFetch(path, init, tokenOverride);

  if (res.status === 401 && !init._retried && !isAuthEndpoint) {
    const newToken = await triggerRefresh();
    if (newToken) {
      // Retry once with the fresh token.
      res = await doFetch(path, { ...init, _retried: true }, newToken);
    }
  }

  return parseResponse<TResponse>(res);
}

